<template>
  <div>
    <h2 class="u-flex-row u-align-center u-ml-x-small">
      <img
        :alt="providerName + ' logo'"
        :srcset="`/img/auth-providers/${provider}@1x.png, /img/auth-providers/${provider}@2x.png 2x`"
        :src="`/img/auth-providers/${provider}@1x.png`"
        class="u-mr-medium"
        style="height: 2.8rem;"
      >
      <span>
        {{ providerName }} authentication
      </span>
    </h2>

    <div class="panel">
      <h3>{{ providerName }} app</h3>
      <p>To configure Single-Sign on, you'll need to create an app for Blendr.io on Okta. Use the following settings:</p>
      <dl>
        <dt>Base URI (optional)</dt>
        <dd><kbd>https://{{ baseUrl }}</kbd></dd>
      </dl>
      <dl>
        <dt>Login redirect URI</dt>
        <dd><kbd>https://{{ baseUrl }}/login/{{ settings['slug'] }}</kbd></dd>
      </dl>
      <dl>
        <dt>Logout redirect URI (optional) </dt>
        <dd><kbd>https://{{ baseUrl }}/logout</kbd></dd>
      </dl>
      <dl>
        <dt>Initiate login URI (optional) </dt>
        <dd><kbd>https://{{ baseUrl }}/login/{{ settings['slug'] }}</kbd></dd>
      </dl>
    </div>

    <div class="panel">
      <h3>{{ providerName }} settings</h3>
      <form @submit.prevent="save">
        <div class="form-group">
          <label for="metadata_url">
            Metadata URL
          </label>
          <input
            id="metadata_url"
            v-model="settings['openId']['metadata_url']"
            type="text"
            class="form-control"
            required
          >
          <span class="help-block">
            Enter your Metadata URL , e.g.<br>
            <kbd>https://subdomain.okta.com/oauth2/default/.well-known/openid-configuration<br>https://subdomain.okta.com</kbd>
          </span>
        </div>

        <div class="form-group">
          <label for="client_id">
            Client ID
          </label>
          <input
            id="client_id"
            v-model="settings['openId']['client_id']"
            type="text"
            class="form-control"
            required
          >
        </div>

        <div class="form-group">
          <label for="client_secret">
            Client secret
          </label>
          <input
            id="client_secret"
            v-model="settings['openId']['client_secret']"
            type="text"
            class="form-control"
            required
          >
        </div>

        <div class="u-flex-row u-mt-x-large">
          <a href="/account/auth" class="btn">
            Cancel
          </a>
          <button type="submit" class="btn btn--primary">
            Save
          </button>
        </div>
      </form>
    </div>
    <div class="panel">
      <template v-if="active">
        <h3>
          Disable {{ providerName }} Authentication
        </h3>
        <p>
          Members of your organization will not be able to authenticate anymore with their current accounts using {{ providerName }} authentication. They might still be able to login without Authentication Provider using their email and password as credentials.
        </p>
        <button class="btn btn--danger u-mt-x-large" @click="disable">
          Disable {{ providerName }} authentication
        </button>
      </template>
      <template v-else>
        <h3>
          Enable {{ providerName }} authentication
        </h3>
        <p>
          Members of your organization will be able to authenticate to Blendr.io via {{ providerName }}.
        </p>
        <button class="btn btn--primary u-mt-x-large" :disabled="!canEnable" @click="enable">
          Enable {{ providerName }} authentication
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import { axios } from '@qlik-trial/automations-ui'
import { MessageLevel } from '@qlik-trial/automations-ui'

export default {
  props: {
    initialSettings: {
      required: true,
      type: Object,
    },
    provider: {
      required: true,
      type: String,
    },
  },

  data() {
    return {
      settings: cloneDeep(this.initialSettings),
    }
  },

  computed: {
    baseUrl() {
      return window.location.hostname
    },

    providerName() {
      return this.titleCase(this.provider)
    },

    active() {
      return this.provider === this.settings['current_provider']
    },

    canEnable() {
      return this.initialSettings['openId']
        && this.initialSettings['openId']['client_id']
        && this.initialSettings['openId']['client_secret']
        && this.initialSettings['openId']['metadata_url']
    },
  },

  methods: {
    save() {
      axios.post('/account/auth/', this.settings)
        .then(_response => {
          this.flash({
            message: `${this.providerName} authentication settings were saved`,
            level: MessageLevel.SUCCESS,
          })
          this.initialSettings = this.settings
        })
        .catch(_error => {
          this.flash({
            message: 'An error occurred saving authentication settings',
            level: MessageLevel.DANGER,
          })
          this.reset()
        })
    },

    enable() {
      this.settings['current_provider'] = this.provider

      axios.post('/account/auth', this.settings)
        .then(_response => {
          this.flash({
            message: `${this.providerName} authentication was enabled`,
            level: MessageLevel.SUCCESS,
          })
          this.initialSettings = this.settings
        })
        .catch(_error => {
          this.flash({
            message: `An error occurred enabling ${this.providerName} authentication`,
            level: MessageLevel.DANGER,
          })
          this.reset()
        })
    },

    disable() {
      this.settings['current_provider'] = null

      axios.post('/account/auth', this.settings)
        .then(_response => {
          this.flash({
            message: `${this.providerName} authentication was disabled`,
            level: MessageLevel.SUCCESS,
          })
          this.initialSettings = this.settings
        })
        .catch(_error => {
          this.flash({
            message: `An error occurred disabling ${this.providerName} authentication`,
            level: MessageLevel.DANGER,
          })
          this.reset()
        })
    },

    reset() {
      this.settings = cloneDeep(this.initialSettings)
    },
  },
}
</script>
